/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {Component, Model} from 'vue-property-decorator';
import {LservVue} from '@/shell/LservVue';
import {
  CompositeFilterCriterion,
  FilterCriterion,
  FilterId,
  FilterValue
} from '@/shell/lists/filter/FilterValue';

/**
 * A base class for filter components.
 */
@Component
export class FilterVue<I, C extends FilterCriterion<I>> extends LservVue {

  /**
   * The filtering status (parameters) to use for the filter.
   *
   * When the current filter criterion is changed by the actual filter component implementation,
   * the filter component emits the `input` event with an updated `FilterValue. Implementations
   * of this class can call just assign to the `criterion` property of this class to emit
   * the respective event.
   *
   * Note that to simplify creating the composite filters, it is possible to either assign the
   * respective `FilterValue` that belongs to this filter component to this property (corresponds
   * to the filter's `filterId` property), or specify the parent composite filter's value
   * (a `FilterValue` instance with a `CompositeFilterCriterion` inside).
   */
  @Model('input')
  readonly value?: FilterValue<I, C>;

  /**
   * A unique ID of the filter instance. Can in particular be used for implementing
   * composite filters.
   */
  protected get filterId(): FilterId {
    // not marking this method as abstract since the required @Component decorator would not
    // work with it
    throw new Error('The `filterId()` method must be implemented');
  }

  /**
   * Gets the `FilterCriterion` from the `FilterValue` objet that belongs to this filter component.
   *
   * Due to the fact that the `value` prop can be bound to the parent composite filter's `value`
   * prop (for convenience of implementing composite filters), this method has some logic to locate
   * the component's own `FilterValue` in such cases.
   */
  protected get criterion(): C | null {
    const value = this.value || this.newFilterValue(null);
    const criterion = value.criterion;
    if (value.filterId === this.filterId) {
      return criterion;
    }
    if (criterion === null) {
      // a composite filter's filter value can have a null criterion when none of its nested
      // filters are active, so just returning null criterion for such nested filter, which
      // signifies such situation
      return null;
    }
    if (criterion instanceof CompositeFilterCriterion) {
      const nestedFilterValue = criterion.getNestedFilterValue(this.filterId);
      const criterionFoundInComposite = nestedFilterValue?.criterion;
      if (criterionFoundInComposite != undefined) {
        return criterionFoundInComposite as C;
      } else {
        // filter criterion for this filter is absent in the parent composite filter, which is
        // a valid situation when a parent was in an all-accepting state or not initialized for
        // the first time yet
        return null;
      }
    } else {
      throw (
          "Invalid FilterValue was supplied via the filter's `value` prop: " +
          `expected '${this.filterId}' but was '${value.filterId}'`
      );
    }
  }

  /**'
   * Emits the `input` event, which reports the newly updated filter criterion. This is a "write"
   * part of the filter value binding, which complements the "read" part represented by
   * the `value` prop.
   * @param criterion a `FilterCriterion` implementation that reflects the updated filter's state,
   *                  or `null` in case when the filter was updated to accept all items.
   */
  protected set criterion(criterion: C | null) {
    this.$emit('input', this.newFilterValue(criterion));
  }

  private newFilterValue(criterion: C | null): FilterValue<I, C> {
    return new FilterValue<I, C>(this.filterId, criterion);
  }
}
